<!-- PWA Install Prompt -->
<div 
  *ngIf="showPrompt" 
  class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-slide-up"
  role="dialog"
  aria-labelledby="pwa-install-title"
  aria-describedby="pwa-install-description"
>
  <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    <!-- Header with gradient -->
    <div class="bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-800 p-4">
      <div class="flex items-center justify-between text-white">
        <div class="flex items-center space-x-3">
          <div class="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
            <svg class="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
            </svg>
          </div>
          <div>
            <h3 id="pwa-install-title" class="font-bold text-lg">Install {{ companyName }}</h3>
            <p class="text-xs text-white/90">Get the best experience</p>
          </div>
        </div>
        <button 
          (click)="dismiss()" 
          class="text-white/80 hover:text-white transition-colors"
          aria-label="Close install prompt"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="p-4">
      <p id="pwa-install-description" class="text-sm text-gray-600 dark:text-gray-300 mb-4">
        Install our app for:
      </p>
      
      <div class="space-y-2 mb-4">
        <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
          <svg class="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <span>⚡ Faster performance</span>
        </div>
        <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
          <svg class="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <span>📱 Works offline</span>
        </div>
        <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
          <svg class="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <span>🏠 Easy access from home screen</span>
        </div>
        <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
          <svg class="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          <span>🔔 Push notifications</span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-2">
        <button
          (click)="install()"
          [disabled]="isInstalling"
          class="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-800 text-white px-4 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          <svg 
            *ngIf="!isInstalling" 
            class="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          <svg 
            *ngIf="isInstalling" 
            class="w-5 h-5 animate-spin" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ isInstalling ? 'Installing...' : 'Install Now' }}</span>
        </button>
        <button
          (click)="remindLater()"
          class="px-4 py-3 rounded-lg font-semibold border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
        >
          Later
        </button>
      </div>
    </div>
  </div>
</div>











